<script setup lang="ts">
  import type { PhysicalLocation } from '@/entities/Document';
  import {
    DefaultTable,
    HeaderTableWithButtons,
    NoDocuments,
    TableHeader,
    TableLoading,
  } from '@/shared/ui';
  import type { OrderOptions } from '@/shared/lib';
  import PhysicalLocationsTableRow from './PhysicalLocationsTableRow.vue';

  type PhysicalLocationTableItem = Pick<PhysicalLocation, 'id' | 'name'>;

  const props = withDefaults(
    defineProps<{
      items: PhysicalLocationTableItem[];
      loading?: boolean;
      orderOptions?: OrderOptions[];
    }>(),
    {
      items: () => [],
      loading: false,
      orderOptions: () => [],
    }
  );

  const emit = defineEmits<{
    'update:sort': [sortCamp: string, orderFlow: string];
    edit: [id: number];
    delete: [id: number];
  }>();
</script>

<template>
  <DefaultTable data-testid="physical-locations-table">
    <thead>
      <tr>
        <HeaderTableWithButtons
          id="header-table-with-buttons"
          colspan="2"
          text="Locais"
          :order-options="orderOptions"
          :has-filter="false"
          @update:sort="(s, o) => emit('update:sort', s, o)"
        />
      </tr>
      <tr v-if="!loading && items.length > 0">
        <TableHeader
          text="Nome"
          th-class="pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 whitespace-nowrap align-middle"
        />
        <TableHeader
          text="Ações"
          th-class="pl-6 pr-[41px] py-4.5 text-right text-xs font-semibold text-gray-07 whitespace-nowrap align-middle w-[164px]"
        />
      </tr>
    </thead>

    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <tr v-if="loading">
        <td colspan="2">
          <TableLoading
            data-testid="physical-locations-loading"
            :row-size="5"
            :column-size="2"
          />
        </td>
      </tr>
      <tr v-else-if="items.length === 0">
        <td colspan="2">
          <NoDocuments
            data-testid="physical-locations-empty"
            title="Ops! Parece que ainda não há conteúdo cadastrado"
          />
        </td>
      </tr>
      <template v-else>
        <PhysicalLocationsTableRow
          v-for="item in props.items"
          :key="item.id"
          :item="item"
          @edit="emit('edit', $event)"
          @delete="emit('delete', $event)"
        />
      </template>
    </tbody>
  </DefaultTable>
</template>
